import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import {
    type CategoryBreakdownDto,
    type ProductsResponseDto,
    type SavingsInsightsSummaryDto,
    getReportSavingsLastRequestParams,
    setSavingsInsightsLastRequestParams,
} from 'entities/ReportSavingsSummary';
import { $api } from 'shared/api/api';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useSavingsSummaryReportVisible } from 'shared/lib/hooks/useSavingsSummaryReportVisible';

interface UseReportsPageSavingsSummaryCategoryResult {
    isSavingsSummaryVisible: boolean | undefined;
    categoryId: string | undefined;
    categoryName: string;
    loading: boolean;
    summary: SavingsInsightsSummaryDto | null;
    byCategory: CategoryBreakdownDto[];
    products: ProductsResponseDto | null;
    totalSaved: number;
    totalSavingsPercent: number;
    orderCount: number;
    productsCount: number;
    dateFrom: Date;
    dateTo: Date;
    datePreset?: string;
    lastRequestParamsPresent: boolean;
}

interface SavingsInsightsRequestBody {
    dateFrom: string;
    dateTo: string;
    propertyIds?: string[];
    categoryIds?: string[];
}

const buildRequestParamsKey = (params: {
    datePreset?: string;
    dateFrom: string;
    dateTo: string;
    propertyIds?: string[];
    categoryIds?: string[];
}): string =>
    JSON.stringify({
        datePreset: params.datePreset,
        dateFrom: params.dateFrom,
        dateTo: params.dateTo,
        propertyIds: params.propertyIds,
        categoryIds: params.categoryIds,
    });

const toNumber = (value: string | number | undefined): number => {
    if (typeof value === 'number') {
        return Number.isNaN(value) ? 0 : value;
    }

    const parsed = parseFloat(value ?? '');
    return Number.isNaN(parsed) ? 0 : parsed;
};

const getSavedProductsCount = (
    products: ProductsResponseDto | null,
): number => {
    if (!products?.items?.length) {
        return 0;
    }

    return products.items.filter((item) => toNumber(item.totalSaved) > 0)
        .length;
};

export const useReportsPageSavingsSummaryCategory =
    (): UseReportsPageSavingsSummaryCategoryResult => {
        const dispatch = useAppDispatch();
        const { categoryId } = useParams<{ categoryId: string }>();
        const location = useLocation();
        const isSavingsSummaryVisible = useSavingsSummaryReportVisible();
        const lastRequestParams = useSelector(
            getReportSavingsLastRequestParams,
        );

        const locationState = location.state as {
            categoryTitle?: string;
            contextTotalSavings?: string;
        } | null;
        const categoryTitleFromState = locationState?.categoryTitle;
        const contextTotalSavings = toNumber(
            locationState?.contextTotalSavings,
        );

        const [loading, setLoading] = useState(false);
        const [summary, setSummary] =
            useState<SavingsInsightsSummaryDto | null>(null);
        const [byCategory, setByCategory] = useState<CategoryBreakdownDto[]>(
            [],
        );
        const [products, setProducts] = useState<ProductsResponseDto | null>(
            null,
        );

        const requestBody = useMemo<SavingsInsightsRequestBody | null>(() => {
            if (!lastRequestParams || !categoryId) {
                return null;
            }

            return {
                dateFrom: lastRequestParams.dateFrom,
                dateTo: lastRequestParams.dateTo,
                categoryIds: [categoryId],
                ...(lastRequestParams.propertyIds?.length
                    ? {
                          propertyIds: lastRequestParams.propertyIds,
                      }
                    : {}),
            };
        }, [categoryId, lastRequestParams]);

        const targetLastRequestParams = useMemo(() => {
            if (!requestBody || !lastRequestParams) {
                return null;
            }

            return {
                datePreset: lastRequestParams.datePreset,
                dateFrom: requestBody.dateFrom,
                dateTo: requestBody.dateTo,
                propertyIds: requestBody.propertyIds,
                categoryIds: requestBody.categoryIds,
            };
        }, [requestBody, lastRequestParams]);

        const currentLastRequestParamsKey = useMemo(() => {
            if (!lastRequestParams) {
                return null;
            }

            return buildRequestParamsKey(lastRequestParams);
        }, [lastRequestParams]);

        const targetLastRequestParamsKey = useMemo(() => {
            if (!targetLastRequestParams) {
                return null;
            }

            return buildRequestParamsKey(targetLastRequestParams);
        }, [targetLastRequestParams]);

        useEffect(() => {
            if (!targetLastRequestParams || !targetLastRequestParamsKey) {
                return;
            }

            if (currentLastRequestParamsKey === targetLastRequestParamsKey) {
                return;
            }

            dispatch(
                setSavingsInsightsLastRequestParams(targetLastRequestParams),
            );
        }, [
            dispatch,
            currentLastRequestParamsKey,
            targetLastRequestParams,
            targetLastRequestParamsKey,
        ]);

        useEffect(() => {
            if (!requestBody) {
                return;
            }

            console.log('requestBody', requestBody);

            let cancelled = false;

            const load = async () => {
                setLoading(true);

                try {
                    const [
                        summaryResponse,
                        byCategoryResponse,
                        productsResponse,
                    ] = await Promise.all([
                        $api.post<SavingsInsightsSummaryDto>(
                            '/v1/reports/savings-insights/summary',
                            requestBody,
                        ),
                        $api.post<{ items: CategoryBreakdownDto[] }>(
                            '/v1/reports/savings-insights/by-category',
                            requestBody,
                        ),
                        $api.post<ProductsResponseDto>(
                            '/v1/reports/savings-insights/products',
                            {
                                ...requestBody,
                                orderBy: '-total_saved',
                                page: 1,
                                pageSize: 25,
                            },
                        ),
                    ]);

                    if (cancelled) {
                        return;
                    }

                    setSummary(summaryResponse.data);
                    setByCategory(byCategoryResponse.data.items ?? []);
                    setProducts(productsResponse.data);
                } finally {
                    if (!cancelled) {
                        setLoading(false);
                    }
                }
            };

            void load();

            return () => {
                cancelled = true;
            };
        }, [requestBody]);

        const categoryName = useMemo(() => {
            if (categoryTitleFromState) {
                return categoryTitleFromState;
            }

            const responseCategoryTitle = byCategory.find(
                (item) => item.categoryId === categoryId,
            )?.categoryTitle;

            if (responseCategoryTitle) {
                return responseCategoryTitle;
            }

            return categoryId ?? '';
        }, [byCategory, categoryId, categoryTitleFromState]);

        const totalSaved = useMemo(
            () => toNumber(summary?.savingsAmount),
            [summary?.savingsAmount],
        );

        const totalSavingsPercent =
            contextTotalSavings > 0
                ? (totalSaved / contextTotalSavings) * 100
                : 100;
        const orderCount = summary?.orderCount ?? 0;
        const productsCount = useMemo(
            () => getSavedProductsCount(products),
            [products],
        );

        const dateFrom = useMemo(
            () =>
                lastRequestParams
                    ? new Date(lastRequestParams.dateFrom)
                    : new Date(),
            [lastRequestParams],
        );
        const dateTo = useMemo(
            () =>
                lastRequestParams
                    ? new Date(lastRequestParams.dateTo)
                    : new Date(),
            [lastRequestParams],
        );

        return {
            isSavingsSummaryVisible,
            categoryId,
            categoryName,
            loading,
            summary,
            byCategory,
            products,
            totalSaved,
            totalSavingsPercent,
            orderCount,
            productsCount,
            dateFrom,
            dateTo,
            datePreset: lastRequestParams?.datePreset,
            lastRequestParamsPresent: Boolean(lastRequestParams),
        };
    };
